import React, { useEffect } from "react";
import { ElementPreview } from "./Elements/Element/ElementPreview";
import {
  BackgroundType,
  Breakpoint,
  FluidSection,
  PageContext,
  WuiltContext,
} from "@/shared/types";
import {
  getSectionStyles,
  backgroundVideoOverlayStyles,
} from "@/shared/utils/styles";
import { VideoBackground } from "@/shared/Components/VideoBackground";
import { useIsIphone } from "@/shared/hooks/useIsIphone";

interface FluidSectionViewerProps {
  sectionData: FluidSection;
  sectionId: string;
  pageContext: PageContext;
  wuiltContext: WuiltContext;
}

export const FluidSectionViewer: React.FC<FluidSectionViewerProps> = ({
  sectionData,
  sectionId,
  pageContext,
  wuiltContext,
}: FluidSectionViewerProps) => {
  const isFirstAndNavTransparent =
    pageContext?.props?.isFirstAndNavTransparent || false;
  const sectionContainerRef = React.useRef<HTMLDivElement>(null);
  const gridContainerRef = React.useRef<HTMLDivElement>(null);
  const [navbarHeight, setNavbarHeight] = React.useState<number>(160);
  const [breakpoint, setBreakpoint] = React.useState<Breakpoint>(
    Breakpoint.Desktop
  );
  const [gridContainerWidth, setGridContainerWidth] = React.useState<
    number | null
  >(() => {
    // Default width based on viewport (prevents 0 on initial render)
    if (typeof window === "undefined") {
      return null;
    } else {
      return window.innerWidth > 1280 ? 1280 : window.innerWidth * 0.9;
    }
  });

  const isIphone = useIsIphone();

  const updateBreakpoint = () => {
    setBreakpoint(
      window.innerWidth <= 767
        ? Breakpoint.Mobile
        : window.innerWidth >= 768 && window.innerWidth <= 1025
        ? Breakpoint.Tablet
        : window.innerWidth >= 1026
        ? Breakpoint.Desktop
        : Breakpoint.Desktop
    );
  };

  useEffect(() => {
    if (document) {
      const navbar = document.getElementById("navbar-wrapper-container");
      setNavbarHeight(navbar?.clientHeight ?? 160);
    }
  }, []);

  useEffect(() => {
    const updateWidth = () => {
      if (gridContainerRef.current) {
        setGridContainerWidth(gridContainerRef.current.clientWidth);
        updateBreakpoint();
      }
    };

    // Update section container width on mount
    updateWidth();

    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [breakpoint]);

  const sectionSettings = sectionData?.settings;
  const sectionSettingsStyles = getSectionStyles(
    sectionSettings?.styles,
    isIphone
  );
  const shouldFullWidth = sectionSettings?.grid?.shouldFullWidth;

  const rows =
    breakpoint === Breakpoint.Mobile
      ? sectionSettings?.grid?.breakpoints?.[breakpoint].rows ?? 10
      : sectionSettings?.grid?.breakpoints?.[breakpoint].rows ?? 10;

  const columns = breakpoint === Breakpoint.Mobile ? 8 : 24;

  const paddingTop = sectionSettings?.styles?.padding?.top ?? 0;
  const paddingBottom = sectionSettings?.styles?.padding?.bottom ?? 0;
  const gridMaxWidth = breakpoint === Breakpoint.Desktop ? "1280px" : "90%";

  const modifiedPaddingTop = isFirstAndNavTransparent
    ? navbarHeight + paddingTop
    : paddingTop;

  return (
    <div
      ref={sectionContainerRef}
      style={{
        display: "flex",
        width: "100%",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        position: "relative",
        paddingTop: `${modifiedPaddingTop}px`,
        paddingBottom: `${paddingBottom}px`,
        ...sectionSettingsStyles,
      }}
    >
      <div
        style={{
          position: "absolute",
          top: "0",
          left: "0",
          width: "100%",
          height: "100%",
          overflow: "hidden",
        }}
      >
        {sectionSettings?.styles?.background?.type === BackgroundType.Video &&
          sectionSettings?.styles?.background?.video?.src && (
            <VideoBackground
              sectionContainerRef={sectionContainerRef}
              backgroundSettings={sectionSettings?.styles?.background}
            />
          )}

        {sectionSettings?.styles?.background?.type === BackgroundType.Video &&
          sectionSettings?.styles?.background?.video?.overlay?.isEnabled && (
            <div
              style={{
                ...backgroundVideoOverlayStyles(
                  sectionSettings?.styles?.background?.video
                ),
              }}
            />
          )}
      </div>
      <div
        ref={gridContainerRef}
        style={{
          display: "grid",
          width: shouldFullWidth ? "100%" : gridMaxWidth,
          position: "relative",
          maxWidth: shouldFullWidth ? "100%" : gridMaxWidth,
          rowGap: sectionSettings?.grid?.rowGap,
          columnGap: sectionSettings?.grid?.columnGap,
          gridTemplateRows: `repeat(${rows}, 30px)`,
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
        }}
      >
        {sectionData?.elements?.map((element) => {
          const isElementVisible = element?.layout?.[breakpoint]?.isVisible;
          const shouldShowElement = isElementVisible ?? true;
          return shouldShowElement ? (
            <ElementPreview
              key={element.id}
              element={element}
              type={element?.type!}
              text={sectionData?.text}
              sectionId={sectionId}
              sectionValueId={sectionData?.id}
              breakpoint={breakpoint}
              wuiltContext={wuiltContext}
              gridMaxWidth={gridMaxWidth}
              gridContainerWidth={gridContainerWidth ?? 0}
            />
          ) : null;
        })}
      </div>
    </div>
  );
};
